import { type RunVisibility } from '@d2/schema';

/**
 * What a client says when it asks the matchmaker for a room.
 *
 * Colyseus gives a create exactly one options object and hands the *same* one
 * to the room's `onCreate` and to its `onAuth`. Two unrelated questions
 * therefore travel together — what this floor should be, and who is asking to
 * sit on it — and the room answers them with two different gates. So the object
 * has two halves, and this module is the one place that says which is which.
 *
 * ## Why the credentials are nested
 *
 * They used to sit flat, beside the seed, and the seat gate reads the join
 * fail-closed: any key it did not ask for fails the whole join. A create that
 * also offered a `seed`, a `population` or a `fixtureKey` was therefore refused
 * before the room existed, which cost this client the ability to choose a floor
 * at all and disabled the co-op suite along with it (`TODO.md` #410). Widening
 * that gate's list would have made it a list that has to be revisited every
 * time the room grows a create option — and one that fails open the day
 * somebody forgets. Nesting is the version that does not: the credentials are
 * an object of their own, `apps/game/src/auth/seat.ts` reads that object and
 * nothing else, and it is still strict inside it.
 *
 * ## Asking is not getting
 *
 * Every field here is a *request*. The room re-decides all of it: the seed is
 * honoured because choosing a cave is nothing to gain, and the population and
 * the **floor size** are refused to every caller that cannot present the key the
 * server process was started with — which no production server holds. Nothing is
 * validated twice here, because a rule checked on both sides is a rule the two
 * sides can disagree about.
 *
 * There is one shape this module *does* decide, and it is not a second copy of
 * anything: a size travels as a pair or not at all. That is not the server's
 * bound being restated — `MIN_FLOOR_SIZE` and `MAX_FLOOR_SIZE` are nowhere in
 * this file — it is this client declining to send a request whose only possible
 * answer is a silent refusal. See `FloorSize`.
 */

/**
 * The key the room reads a join's credentials from.
 *
 * The one string this module and `apps/game/src/auth/seat.ts` have to agree on,
 * and it is stated in both because `apps/web` cannot import the server. It
 * belongs beside `ROOM` and `CLIENT_MESSAGE` in `@d2/schema`, which is where
 * every other name both sides have to agree on already lives — recorded as
 * `TODO.md` #440 rather than done here, because the wire package is not this
 * branch's to change.
 */
export const SEAT_KEY = 'auth';

/** Who is asking to sit down. Opaque to this module: it forwards, it never inspects. */
export interface SeatCredentials {
  readonly token: string;
  readonly characterId: string;
}

/**
 * How big a floor is, in cells.
 *
 * **One value rather than two fields**, and that is the whole design of it. The
 * room answers a request naming one dimension `malformed` — *"half a size is not
 * a size"* (`apps/game/src/rooms/intents.ts`, `resolveFloorSize`) — and then
 * grows the floor it was always going to grow, saying nothing. So a client that
 * could hold half a size would have a way to ask for a small floor, be handed
 * the played one, and never learn the difference: seventeen seconds of walking
 * turning silently into thirty-seven. A pair that only exists whole cannot
 * express the request that gets that answer.
 *
 * The two numbers are `DungeonOptions`' own and `LevelState` publishes them, so
 * this is the shape the floor is already described in on the way back.
 */
export interface FloorSize {
  readonly width: number;
  readonly height: number;
}

/** What a *create* asks the room for, as the URL stated it (`./launch`). */
export interface FloorRequest {
  readonly credentials: SeatCredentials;
  /** The cave to grow. Always offered — a client that names no seed still launched with one. */
  readonly seed: number;
  /** How many monsters to ask for, or `null` to leave the floor to the server. */
  readonly population: number | null;
  /**
   * How big a floor to ask for, or `null` to leave the size to the server.
   *
   * A **test fixture**, refused on the population's terms and for a sharper
   * reason: the stairs sit at the greatest walking distance from the entrance,
   * so a floor's size is the length of its run and a client that could shrink
   * its own would descend as fast as it could click (`@d2/schema`'s
   * `DescentOptions`).
   */
  readonly size: FloorSize | null;
  /** The authorisation to offer with it, or `null` when the launch carried none. */
  readonly fixtureKey: string | null;
  /**
   * Who may find this run — or `null` because nobody chose, which the server
   * reads as **private** (`@d2/schema`'s `RUN_VISIBILITY`).
   *
   * The one request on this object the server grants to everybody, and the only
   * one a *player* makes rather than a URL. That asymmetry is why it is a field
   * here at all: a population and a floor size are advantages a client would be
   * taking from the game, so they are refused to anybody without the server's
   * own key, while who may find your run is nothing another player could not
   * have by asking for it too.
   *
   * `null` is not the same as asking for a private run and this module does not
   * make it so — see `createOptions`. What being listed *costs* is the
   * character names and levels of everybody in the party, published to anybody
   * holding an account (`RunListing`), so it has to be a thing somebody chose.
   */
  readonly visibility: RunVisibility | null;
}

/** The object the matchmaker is handed. Untyped values on purpose: the room believes none of it. */
export type RoomOptions = Readonly<Record<string, unknown>>;

/**
 * The options for a room this client is **creating**.
 *
 * A request nobody made is left *off* rather than sent as `null`, and that is
 * not tidiness. `undefined` does not survive msgpack, and the room tells an
 * absent population ("unstated" — the ordinary path every client takes) from a
 * stated one it will not grant ("unauthorised"). A client that always sent the
 * key would make every ordinary create look like a refused request.
 *
 * The size follows that rule twice over: both dimensions travel or neither
 * does. The room reads "unstated" off **both** being absent, so a lone `width`
 * is not a smaller ask than a pair — it is a *malformed* one, refused, and
 * answered with the floor the room would have grown anyway (see `FloorSize`).
 * The two keys are written out rather than spread, so the only thing that can
 * reach the matchmaker under this name is a size this module built.
 *
 * **The visibility is left off the same way, and here the silence is the safe
 * answer rather than merely the honest one.** `resolveVisibility` reads an
 * absent or unrecognised value as *private*, so a create that says nothing is a
 * run nobody can find but its own party — which is what every client that
 * predates this field was already getting.
 *
 * The two paths therefore agree by construction. A caller with a control on
 * screen states what the control shows, and today that is `private` until a
 * player says otherwise (`ui/town-screen.tsx`); a caller with no control at all
 * — the renderer sandbox, a `?room=` launch — states nothing and is given the
 * same run. What cannot happen either way is a run being *advertised* because a
 * value went missing, and that is the only asymmetry that matters: being listed
 * publishes the party's names.
 */
export const createOptions = ({
  credentials,
  seed,
  population,
  size,
  fixtureKey,
  visibility,
}: FloorRequest): RoomOptions => ({
  [SEAT_KEY]: credentials,
  seed,
  ...(population === null ? {} : { population }),
  ...(size === null ? {} : { width: size.width, height: size.height }),
  ...(fixtureKey === null ? {} : { fixtureKey }),
  ...(visibility === null ? {} : { visibility }),
});

/**
 * The options for a room that already exists.
 *
 * Credentials only. That room's floor was grown before this client arrived and
 * its create options are never offered again, so a seed or a population sent
 * here would be a request nothing reads — and a request nothing reads is worse
 * than no request at all, because it looks like one that was refused.
 *
 * **The visibility is not on it either**, and that is the sharpest case of the
 * rule rather than an exception to it: whether a run is advertised was decided
 * by whoever created it, is written into the room's matchmaking metadata once,
 * and is not a thing an arriving player gets a second vote on. A `visibility`
 * here would be a client asking to publish — or unpublish — somebody else's
 * party.
 */
export const joinOptions = (credentials: SeatCredentials): RoomOptions => ({ [SEAT_KEY]: credentials });
